import moment from "moment-timezone";
import {
  formatChange,
  convertTo24HourFormat,
  convertTo12,
  formatTimeTo24Hr,
  isValidTimeFormat,
  isEndTimeGreater,
  convertTo24Hour,
} from "../services/timeFormat.js";
import { formatTime, timeFormatChange } from "../services/trackerServices.js";

/**
 * Parse time input and convert to standard format
 * @param {string} input - Time input string
 * @returns {string|boolean} Formatted time string or false if invalid
 */
export const parseTimeInput = (input) => {
  input = input.trim().toLowerCase();
  let timeRegex = /^(\d{1,2}):?(\d{2})?\s*([ap]m?)?$/;
  let match = input.match(timeRegex);

  if (!match) {
    timeRegex = /^(\d{1,2}):?(\d{1,2})?\s*([ap]m?)?$/;
    match = input.match(timeRegex);
    if (!match) return false;
  }

  let [_, hour, minutes, period] = match;
  hour = hour.padStart(2, "0");
  minutes = minutes ? minutes.padStart(2, "0") : "00";
  if (
    parseInt(hour, 10) > 12 ||
    parseInt(hour, 10) < 1 ||
    parseInt(minutes, 10) > 59
  ) {
    return false;
  }
  period = period
    ? period?.toUpperCase()
    : parseInt(hour, 10) < 12
      ? "AM"
      : "PM";

  return `${hour}:${minutes} ${period}`;
};

/**
 * Format time input
 * @param {string} time - Time string
 * @returns {string|boolean} Formatted time or false if invalid
 */
export const formatTimeInput = (time) => {
  const input = parseTimeInput(time);
  if (input) {
    const momentTime = moment(input, "hh:mm A");
    if (!momentTime.isValid()) return false;
    return momentTime.format("hh:mm A");
  }
  return false;
};

/**
 * Validate custom time format
 * @param {string} timeString - Time string to validate
 * @returns {boolean} True if valid
 */
export const isValidCustomTimeFormat = (timeString) => {
  const customTimeRegex = /^(0?[1-9]|1[0-2])[apAP]$/;
  return customTimeRegex.test(timeString);
};

/**
 * Add one hour to start time
 * @param {string} startTime - Start time string
 * @returns {string} Time string with one hour added
 */
export const addOneHour = (startTime) => {
  const time = moment(startTime, "hh:mm A");
  const newTime = time.add(1, "hour");
  return newTime.format("hh:mm A");
};

/**
 * Subtract one hour from start time
 * @param {string} startTime - Start time string
 * @returns {string} Time string with one hour subtracted
 */
export const subOneHour = (startTime) => {
  const time = moment(startTime, "hh:mm A");
  const newTime = time.subtract(1, "hour");
  return newTime.format("hh:mm A");
};

/**
 * Convert to standard time format
 * @param {string} timeString - Time string
 * @returns {string} Standard time format
 */
export function convertToStandardTime(timeString) {
  const isPM = timeString.toLowerCase().includes("p");
  const hours = parseInt(timeString, 10);
  if (isNaN(hours)) {
    return "Invalid format";
  }
  const standardTime = moment({ hour: hours % 12, minute: 0 }).format(
    "hh:mm A"
  );
  return isPM ? standardTime.replace("AM", "PM") : standardTime;
}

/**
 * Time format change 2
 * @param {string} time - Time string
 * @returns {string|boolean} Converted time or false
 */
export const timeFomatChange2 = (time) => {
  const input = time.trim().toLowerCase();
  let converted = "";
  // Check if the input matches the supported time format
  if (/^\d{1,2}:\d{2}[ap]$/i.test(input)) {
    const [hour, minutes] = input.match(/\d+/g);
    const isPM = input.includes("p");
    let formattedHour = parseInt(hour, 10);
    if (isPM && formattedHour !== 12) {
      formattedHour += 12;
    }
    if (!isPM && formattedHour === 12) {
      formattedHour = 0;
    }
    formattedHour = formattedHour % 12 || 12;
    converted = `${formattedHour.toString().padStart(2, "0")}:${minutes} ${isPM ? "PM" : "AM"
      }`;
  } else {
    converted = false;
  }
  return converted;
};

/**
 * Create time change handlers for CardBody
 * @param {Object} params - Handler parameters
 * @returns {Object} Time change handlers
 */
export const createCardBodyTimeHandlers = ({
  setStartTimeEdit,
  setEndTimeEdit,
  setStartTimeTemp,
  setEndTimeTemp,
  setStartTimeEdit24,
  setEndTimeEdit24,
  setUDuration,
  setUFormatedTime,
  setUStartTimeError,
  setUEndTimeError,
  setUFormatedTimeError,
  endTimeEdit24,
  startTimeEdit24,
  startTimeEdit,
  endTimeEdit,
}) => {
  /**
   * Change start time (12-hour format)
   */
  const changeStartTime = (startTime) => {
    let formattedTime = timeFormatChange(startTime);
    if (formattedTime === false) {
      setUStartTimeError(true);
      return;
    }

    setStartTimeEdit(formattedTime);
    setStartTimeTemp(formattedTime);
  };

  /**
   * Change end time (12-hour format)
   */
  const changeEndTime = (endTime) => {
    let formattedTime = timeFormatChange(endTime);
    if (formattedTime === false) {
      setUEndTimeError(true);
      return;
    }
    setEndTimeEdit(formattedTime);
    setEndTimeTemp(formattedTime);
  };

  /**
   * Change start time (24-hour format)
   */
  const changeStartTime24 = (startTime) => {
    setStartTimeEdit24(startTime);
    if (formatChange(startTime)) {
      var convertedTim = convertTo24HourFormat(startTime);

      setStartTimeEdit24(convertedTim);
      setStartTimeEdit(convertTo12(convertedTim));
      setUStartTimeError(false);
    } else {
      setUStartTimeError(true);
    }

    var oldTime = null;
    if (
      !isValidTimeFormat(formatTimeTo24Hr(startTime)) ||
      !isValidTimeFormat(formatTimeTo24Hr(endTimeEdit24))
    ) {
      setUFormatedTimeError(true);
    }

    if (
      isEndTimeGreater(
        formatTimeTo24Hr(startTime),
        formatTimeTo24Hr(endTimeEdit24)
      )
    ) {
      const SrtTime = convertTo12(formatTimeTo24Hr(startTime));
      const endTime = convertTo12(formatTimeTo24Hr(endTimeEdit24));
      const format = "h:mm A";
      const time1 = moment(SrtTime, format).tz(global.config.getTimeZone());
      const time2 = moment(endTime, format).tz(global.config.getTimeZone());

      const timeDifferenceInSeconds = time2.diff(time1, "seconds");
      if (timeDifferenceInSeconds > 0) {
        setUDuration(parseInt(timeDifferenceInSeconds));
        setUFormatedTime(
          `${formatTime(
            Math.floor(parseInt(timeDifferenceInSeconds) / 3600)
          )}:${formatTime(
            Math.floor(parseInt((timeDifferenceInSeconds % 3600) / 60))
          )}:${formatTime(Math.floor(parseInt(timeDifferenceInSeconds) % 60))}`
        );
        setUStartTimeError(false);
        setUEndTimeError(false);
        setUFormatedTimeError(false);
      }
    } else {
      setUStartTimeError(true);
      setUFormatedTimeError(true);
    }
  };

  /**
   * Change end time (24-hour format)
   */
  const changeEndTime24 = (endTime) => {
    // Use startTimeEdit24 from closure
    const currentStartTime24 = startTimeEdit24;
    
    setEndTimeEdit24(endTime);
    if (formatChange(endTime)) {
      var convertedTim = convertTo24HourFormat(endTime);

      setEndTimeEdit24(convertedTim);
      setEndTimeEdit(convertTo12(convertedTim));
      setUStartTimeError(false);
    } else {
      setUStartTimeError(true);
    }

    if (
      !isValidTimeFormat(currentStartTime24) ||
      !isValidTimeFormat(convertTo24HourFormat(endTime))
    ) {
      setUFormatedTimeError(true);
    }
    if (isEndTimeGreater(currentStartTime24, convertTo24HourFormat(endTime))) {
      const SrtTime = convertTo12(currentStartTime24);
      const edTime = convertTo12(convertTo24HourFormat(endTime));
      const format = "h:mm A";
      const time1 = moment(SrtTime, format).tz(global.config.getTimeZone());
      const time2 = moment(edTime, format).tz(global.config.getTimeZone());
      const timeDifferenceInSeconds = time2.diff(time1, "seconds");
      if (timeDifferenceInSeconds > 0) {
        setUDuration(parseInt(timeDifferenceInSeconds));
        setUFormatedTime(
          `${formatTime(
            Math.floor(parseInt(timeDifferenceInSeconds) / 3600)
          )}:${formatTime(
            Math.floor(parseInt((timeDifferenceInSeconds % 3600) / 60))
          )}:${formatTime(Math.floor(parseInt(timeDifferenceInSeconds) % 60))}`
        );
        setUStartTimeError(false);
        setUEndTimeError(false);
        setUFormatedTimeError(false);
      }
    } else {
      setUEndTimeError(true);
      setUFormatedTimeError(true);
    }
  };

  return {
    changeStartTime,
    changeEndTime,
    changeStartTime24,
    changeEndTime24,
  };
};
